import { AthosCollectionProvider } from 'libs/athos/fetchers/collection'

import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/athos/hooks/useScrollCache'
import { useFilterScroll } from 'libs/athos/hooks/useFilterScroll'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { BeaconAnalyticsProvider, useBeaconLogImpressions, useBeaconLogRender } from 'libs/athos/hooks/useBeaconAnalytics'
import { useMerchedResults } from 'libs/athos/hooks/useMerchedResults'
import { Pagination } from 'libs/athos/components/collection/Pagination'
import { FiltersProvider } from 'libs/athos/hooks/useFilters'
import { CollectionToolbar } from 'libs/athos/components/collection/CollectionToolbar'
import { CustomFilter } from 'libs/athos/components/collection/filters/CustomFilter'
import { FilterDrawer } from 'libs/athos/components/collection/filters/FilterDrawer'
import { ResultItem } from 'libs/athos/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/athos/components/SkeletonCard'
import { calculateProductCount } from 'libs/athos/components/collection/filters/ProductCount'
import { globalEmitter, emitterProps } from 'helpers/emitter-event'

const GRID_IMAGE_SIZES = '(min-width: 1828px) 25vw, (min-width: 768px) 33vw, 100vw'
const GRID_IMAGE_WIDTHS = [200, 320, 480, 640, 800, 1000, 1200, 1440]

function CollectionGrid({ showFilters, customFilter }) {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const { results, isLoading } = useAthosContext()
  const currentResults = useMerchedResults()
  const { setScrollElement } = useFilterScroll()
  const hasStartedLoading = useRef(false)
  const [beaconNode, setBeaconNode] = useState()

  useBeaconLogRender()
  useBeaconLogImpressions(beaconNode)

  const setGridNode = useCallback((el) => {
    setScrollElement(el)
    setBeaconNode(el)
  }, [setScrollElement])

  const productCount = calculateProductCount(results)

  useEffect(() => {
    if (isLoading) hasStartedLoading.current = true
    if (!isLoading && hasStartedLoading.current) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (!results?.pagination) return
    window.collectionProductCount = productCount
    globalEmitter.emit(emitterProps.collection.count, productCount)
  }, [productCount, results?.pagination])

  useEffect(() => {
    if (!isLoading && currentResults?.length > 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 150)

      return () => clearTimeout(timer)
    } else if (isLoading || firstLoad) {
      setShowSkeleton(true)
    }
  }, [isLoading, currentResults?.length, firstLoad])

  useScrollCache([currentResults, firstLoad])

  return (
    <div className="grow flex flex-col">
      <FiltersProvider>
        {customFilter && <CustomFilter filter={customFilter} />}
        <CollectionToolbar />
        {productCount > 9 && (
          <x-back-to-top className="lg-max:!hidden sticky top-view left-margin size-0 z-40">Back To Top</x-back-to-top>
        )}
        <div className="grow flex flex-col md:grid md:grid-cols-min-full items-stretch">
          <FilterDrawer showFilters={showFilters} />
          <div className="grow flex flex-col h-auto lg:border-l lg:border-borders-default lg-max:pb-4.875">
            <div
              ref={setGridNode}
              className={clsx('grid grid-cols-2 md:grid-cols-3 xxl:grid-cols-4 image-view-small:lg:grid-cols-4 image-view-small:xxl:grid-cols-5 image-view-small:xxxl:grid-cols-4 auto-rows-min [grid-auto-flow:row_dense] gap-x-gutter gap-y-0.25 w-full', (isLoading && firstLoad) && 'min-h-screen-height')}
            >
              {showSkeleton && firstLoad ? (
                Array.from({ length: window.settings?.per_page || 12 }, (_, index) => (
                  <SkeletonCard key={`skeleton-${index}`} className="w-full" />
                ))
              ) : (
                currentResults?.map((item, index) => {
                  return <ResultItem item={item} key={item.handle || `banner-${index}`} className="wk-wishlist-button-target" dataIndex={index.toString()} eager={index === 0} imageSizes={GRID_IMAGE_SIZES} imageWidths={GRID_IMAGE_WIDTHS} mobileSlider />
                })
              )}
            </div>
            <Pagination />
          </div>
        </div>
      </FiltersProvider>
    </div>
  )
}

export function WrappedCollectionGrid({ customFilter, perPage, 'show-filters': showFilters }) {
  const resultsPerPage = perPage || window.settings?.per_page
  scrollHistoryManager.updateHistoryState()
  return (
    <AthosCollectionProvider handle={window.collection?.handle} resultsPerPage={resultsPerPage}>
      <BeaconAnalyticsProvider type="category">
        <CollectionGrid customFilter={customFilter} showFilters={showFilters === 'true'} />
      </BeaconAnalyticsProvider>
    </AthosCollectionProvider>
  )
}
